import { ApiError } from '@/shared/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, Loader2, Play, Redo2, Save, Undo2 } from 'lucide-react';
import { type DragEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  type Connection,
  Controls,
  type Edge,
  MiniMap,
  type Node,
  type NodeChange,
  type EdgeChange,
  type ReactFlowInstance,
  Background,
  BackgroundVariant,
  applyEdgeChanges,
  applyNodeChanges,
  addEdge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useThemeStore } from '@/shared/stores/theme-store';
import { type FlowDefinition, getChatbot, testChatbot, updateChatbot } from '../api';
import { Palette } from './Palette';
import { SidePanel } from './SidePanel';
import { type NodeKind, nodeTypes } from './node-types';

type SaveState = 'idle' | 'saving' | 'saved' | 'dirty' | 'error';

interface HistoryEntry {
  nodes: Node[];
  edges: Edge[];
}

const HISTORY_LIMIT = 50;

export function ChatbotEditorPage() {
  // biome-ignore lint/suspicious/noExplicitAny: dynamic param typing
  const params = useParams({ strict: false }) as any;
  const id = Number(params?.id);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const chatbotQ = useQuery({
    queryKey: ['chatbot', id],
    queryFn: () => getChatbot(id),
    enabled: Number.isFinite(id),
  });

  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = useMemo(
    () => (selectedId ? (nodes.find((n) => n.id === selectedId) ?? null) : null),
    [nodes, selectedId],
  );
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastEditAt, setLastEditAt] = useState<Date | null>(null);
  const [showTestConfirm, setShowTestConfirm] = useState(false);
  const reactFlowWrapper = useRef<HTMLDivElement | null>(null);
  const [rfInstance, setRfInstance] = useState<ReactFlowInstance | null>(null);
  // Modo escuro: props do React Flow (pontos do fundo, máscara do minimap) são
  // cores inline — precisam do estado do tema (CSS sozinho não cobre).
  const isDark = useThemeStore((s) => s.resolved === 'dark');

  // Histórico undo/redo
  const undoStack = useRef<HistoryEntry[]>([]);
  const redoStack = useRef<HistoryEntry[]>([]);
  const skipNextHistory = useRef(false);

  // Atualiza relativeNow a cada minuto pra "última edição há Xmin" rerenderar
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  // Load flow into state
  useEffect(() => {
    if (!chatbotQ.data) return;
    const flow = chatbotQ.data.flow_definition;
    skipNextHistory.current = true;
    setNodes(
      flow.nodes.map((n) => ({
        id: n.id,
        type: n.type,
        position: n.position,
        data: { ...n.data, kind: n.type },
      })),
    );
    setEdges(
      flow.edges.map((e) => {
        const edge: Edge = {
          id: e.id,
          source: e.source,
          target: e.target,
        };
        if (e.sourceHandle) {
          edge.sourceHandle = e.sourceHandle;
          if (e.sourceHandle === 'true') edge.style = { stroke: '#10B981', strokeWidth: 2 };
          else if (e.sourceHandle === 'false') edge.style = { stroke: '#EF4444', strokeWidth: 2 };
        }
        if (e.targetHandle) edge.targetHandle = e.targetHandle;
        if (e.label) edge.label = e.label;
        return edge;
      }),
    );
    undoStack.current = [];
    redoStack.current = [];
  }, [chatbotQ.data]);

  const pushHistory = useCallback((ns: Node[], es: Edge[]) => {
    if (skipNextHistory.current) {
      skipNextHistory.current = false;
      return;
    }
    undoStack.current.push({ nodes: ns, edges: es });
    if (undoStack.current.length > HISTORY_LIMIT) undoStack.current.shift();
    redoStack.current = [];
  }, []);

  const markDirty = useCallback(() => {
    setSaveState((s) => (s === 'saving' ? s : 'dirty'));
    setLastEditAt(new Date());
  }, []);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      const meaningful = changes.some((c) => c.type !== 'select' && c.type !== 'dimensions');
      if (meaningful) pushHistory(nodes, edges);
      setNodes((ns) => applyNodeChanges(changes, ns));
      if (meaningful) markDirty();
    },
    [pushHistory, markDirty, nodes, edges],
  );
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      const meaningful = changes.some((c) => c.type !== 'select');
      if (meaningful) pushHistory(nodes, edges);
      setEdges((es) => applyEdgeChanges(changes, es));
      if (meaningful) markDirty();
    },
    [pushHistory, markDirty, nodes, edges],
  );
  const onConnect = useCallback(
    (conn: Connection) => {
      pushHistory(nodes, edges);
      const newEdge: Edge = {
        ...conn,
        id: `e-${conn.source}-${conn.target}-${Date.now()}`,
      } as Edge;
      // Pinta a edge baseada no handle (true/false)
      if (conn.sourceHandle === 'true') {
        newEdge.style = { stroke: '#10B981', strokeWidth: 2 };
      } else if (conn.sourceHandle === 'false') {
        newEdge.style = { stroke: '#EF4444', strokeWidth: 2 };
      }
      setEdges((es) => addEdge(newEdge, es));
      markDirty();
    },
    [pushHistory, markDirty, nodes, edges],
  );

  const onDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      const kind = event.dataTransfer.getData('application/reactflow-node-kind') as NodeKind;
      if (!kind || !rfInstance) return;
      // Apenas 1 trigger; end pode ter múltiplos
      if (kind === 'trigger' && nodes.some((n) => n.type === 'trigger')) {
        alert('Apenas um nó "trigger" é permitido por fluxo.');
        return;
      }
      pushHistory(nodes, edges);
      const position = rfInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const newNode: Node = {
        id: `n-${kind}-${Date.now()}`,
        type: kind,
        position,
        data: { kind, config: defaultConfigFor(kind) },
      };
      setNodes((ns) => [...ns, newNode]);
      markDirty();
    },
    [rfInstance, nodes, edges, pushHistory, markDirty],
  );

  const updateSelectedConfig = useCallback(
    (config: Record<string, unknown>) => {
      if (!selectedId) return;
      pushHistory(nodes, edges);
      setNodes((ns) =>
        ns.map((n) => (n.id === selectedId ? { ...n, data: { ...n.data, config } } : n)),
      );
      markDirty();
    },
    [selectedId, markDirty, pushHistory, nodes, edges],
  );

  const updateSelectedLabel = useCallback(
    (label: string) => {
      if (!selectedId) return;
      pushHistory(nodes, edges);
      setNodes((ns) =>
        ns.map((n) => (n.id === selectedId ? { ...n, data: { ...n.data, label } } : n)),
      );
      markDirty();
    },
    [selectedId, markDirty, pushHistory, nodes, edges],
  );

  const duplicateSelected = useCallback(() => {
    if (!selected) return;
    if (selected.type === 'trigger') return;
    pushHistory(nodes, edges);
    const newId = `n-${selected.type}-${Date.now()}`;
    const newNode: Node = {
      id: newId,
      type: selected.type,
      position: { x: selected.position.x + 40, y: selected.position.y + 40 },
      data: { ...selected.data },
    };
    setNodes((ns) => [...ns, newNode]);
    markDirty();
  }, [selected, pushHistory, markDirty, nodes, edges]);

  const deleteSelected = useCallback(() => {
    if (!selectedId || !selected) return;
    if (selected.type === 'trigger') return;
    pushHistory(nodes, edges);
    setNodes((ns) => ns.filter((n) => n.id !== selectedId));
    setEdges((es) => es.filter((e) => e.source !== selectedId && e.target !== selectedId));
    setSelectedId(null);
    markDirty();
  }, [selected, selectedId, pushHistory, markDirty, nodes, edges]);

  const undo = useCallback(() => {
    const prev = undoStack.current.pop();
    if (!prev) return;
    redoStack.current.push({ nodes, edges });
    skipNextHistory.current = true;
    setNodes(prev.nodes);
    setEdges(prev.edges);
    markDirty();
  }, [nodes, edges, markDirty]);

  const redo = useCallback(() => {
    const next = redoStack.current.pop();
    if (!next) return;
    undoStack.current.push({ nodes, edges });
    skipNextHistory.current = true;
    setNodes(next.nodes);
    setEdges(next.edges);
    markDirty();
  }, [nodes, edges, markDirty]);

  const buildFlowDef = useCallback((): FlowDefinition => {
    return {
      nodes: nodes.map((n) => ({
        id: n.id,
        type: (n.type ?? 'message') as NodeKind,
        position: n.position,
        data: {
          label: n.data?.label,
          config: n.data?.config ?? {},
        },
      })),
      edges: edges.map((e) => {
        const out: FlowDefinition['edges'][number] = {
          id: e.id,
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle ?? null,
          targetHandle: e.targetHandle ?? null,
        };
        if (typeof e.label === 'string') out.label = e.label;
        return out;
      }),
    };
  }, [nodes, edges]);

  const saveMut = useMutation({
    mutationFn: () => updateChatbot(id, { flow_definition: buildFlowDef() }),
    onMutate: () => {
      setSaveState('saving');
      setSaveError(null);
    },
    onSuccess: () => {
      setSaveState('saved');
      void qc.invalidateQueries({ queryKey: ['chatbot', id] });
      toast.success('Fluxo salvo');
    },
    onError: (err) => {
      setSaveState('error');
      const msg =
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title)
          : 'Erro ao salvar o fluxo';
      setSaveError(msg);
      toast.error(msg);
    },
  });

  const testMut = useMutation({
    mutationFn: () => testChatbot(id),
    onSuccess: (res) => {
      setShowTestConfirm(false);
      alert(`Execução de teste iniciada (#${res.execution_id}).`);
    },
    onError: (err) => {
      if (err instanceof ApiError) alert(err.problem.detail ?? err.problem.title);
    },
  });

  // Atalhos: Ctrl+S, Ctrl+Z, Ctrl+Shift+Z
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const meta = e.ctrlKey || e.metaKey;
      if (meta && e.key === 's') {
        e.preventDefault();
        saveMut.mutate();
      } else if (meta && e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        redo();
      } else if (meta && e.key === 'z') {
        e.preventDefault();
        undo();
      } else if (meta && e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [saveMut, undo, redo]);

  const saveLabel = useMemo(() => {
    switch (saveState) {
      case 'saving':
        return 'Salvando…';
      case 'saved':
        return 'Salvo';
      case 'dirty':
        return 'Alterações não salvas';
      case 'error':
        return saveError ?? 'Erro ao salvar';
      default:
        return 'Salvo';
    }
  }, [saveState, saveError]);

  const lastEditLabel = useMemo(() => {
    if (!lastEditAt) return null;
    const diffMin = Math.floor((now - lastEditAt.getTime()) / 60_000);
    if (diffMin < 1) return 'agora há pouco';
    if (diffMin === 1) return 'há 1 minuto';
    if (diffMin < 60) return `há ${diffMin} minutos`;
    const hours = Math.floor(diffMin / 60);
    return hours === 1 ? 'há 1 hora' : `há ${hours} horas`;
  }, [lastEditAt, now]);

  if (chatbotQ.isLoading) {
    return (
      <div className="flex h-full flex-1 items-center justify-center">
        <Loader2 className="animate-spin text-n-400" />
      </div>
    );
  }
  if (chatbotQ.isError || !chatbotQ.data) {
    return (
      <div className="flex h-full flex-1 items-center justify-center text-n-500">
        Chatbot não encontrado.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-1 flex-col">
      {/* Toolbar topo */}
      <header className="flex items-center gap-3 border-n-200 border-b bg-n-0 px-5 py-2.5">
        <button
          type="button"
          // biome-ignore lint/suspicious/noExplicitAny: dynamic route
          onClick={() => navigate({ to: '/chatbot' as any })}
          className="flex h-8 items-center gap-1.5 rounded border border-n-200 bg-n-0 px-3 font-bold text-[11px] text-n-700 uppercase tracking-[.05em] hover:bg-n-50"
          title="Voltar pra lista"
        >
          <ArrowLeft size={13} strokeWidth={1.75} />
          Voltar pra lista
        </button>
        <div className="flex-1">
          <div className="font-semibold text-[14px] text-n-900">{chatbotQ.data.name}</div>
          <div className="mt-[1px] text-[11px] text-n-500">v{chatbotQ.data.version}</div>
        </div>
        <button
          type="button"
          onClick={() => setShowTestConfirm(true)}
          disabled={!chatbotQ.data.test_phone}
          className="flex h-8 items-center gap-1.5 rounded border border-n-200 bg-n-0 px-3 font-bold text-[11px] text-n-700 uppercase tracking-[.05em] hover:bg-n-50 disabled:cursor-not-allowed disabled:opacity-50"
          title={chatbotQ.data.test_phone ? 'Testar fluxo' : 'Configure celular de teste'}
        >
          <Play size={13} strokeWidth={1.75} />
          Testar
        </button>
        <button
          type="button"
          onClick={() => saveMut.mutate()}
          disabled={saveState === 'saving'}
          className="flex h-8 items-center gap-1.5 rounded bg-brand-500 px-3 font-bold text-[11px] text-white uppercase tracking-[.05em] hover:bg-brand-600 disabled:opacity-50"
        >
          <Save size={13} strokeWidth={1.75} />
          Salvar fluxo
        </button>
      </header>

      {/* Canvas + Palette + SidePanel */}
      <div className="flex flex-1 overflow-hidden">
        <Palette />

        <div ref={reactFlowWrapper} className="flex-1" onDragOver={onDragOver} onDrop={onDrop}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setRfInstance}
            onSelectionChange={({ nodes: selNodes }) => {
              const newId = selNodes[0]?.id ?? null;
              setSelectedId((prev) => (prev === newId ? prev : newId));
            }}
            nodeTypes={nodeTypes}
            fitView
            deleteKeyCode={['Backspace', 'Delete']}
          >
            {/* Sub-toolbar dentro do canvas (mockup-style) */}
            <CanvasSubToolbar
              nodeCount={nodes.length}
              lastEditLabel={lastEditLabel}
              saveState={saveState}
              saveLabel={saveLabel}
              onUndo={undo}
              onRedo={redo}
              canUndo={undoStack.current.length > 0}
              canRedo={redoStack.current.length > 0}
            />
            <Background
              variant={BackgroundVariant.Dots}
              gap={20}
              size={1.5}
              color={isDark ? '#334155' : '#CBD5E1'}
            />
            <Controls />
            <MiniMap
              nodeColor={(n) => {
                const kind = (n.data?.kind ?? n.type) as NodeKind;
                const colors: Record<NodeKind, string> = {
                  trigger: '#334155',
                  message: '#0A4EE3',
                  input: '#EC4899',
                  condition: '#F59E0B',
                  condition_vars: '#0EA5A0',
                  menu: '#6366F1',
                  retry: '#E11D48',
                  schedule_table: '#14B8A6',
                  transfer_human: '#10B981',
                  wait: '#8B5CF6',
                  webhook: '#0EA5E9',
                  end: '#64748B',
                };
                return colors[kind] ?? '#94A3B8';
              }}
              maskColor={isDark ? 'rgba(0,0,0,0.45)' : 'rgba(15,23,42,0.06)'}
            />
          </ReactFlow>
        </div>

        {selected && (
          <SidePanel
            node={selected}
            onChange={updateSelectedConfig}
            onLabelChange={updateSelectedLabel}
            onDuplicate={duplicateSelected}
            onDelete={deleteSelected}
            onClose={() => setSelectedId(null)}
          />
        )}
      </div>

      {showTestConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowTestConfirm(false);
          }}
        >
          <div className="w-full max-w-md rounded-md bg-n-0 p-6 shadow-xl">
            <h3 className="font-bold text-[16px] text-n-900">Testar fluxo</h3>
            <p className="mt-2 text-[13px] text-n-600">
              Vou enviar mensagens reais pra <b>{chatbotQ.data.test_phone}</b> seguindo o fluxo. Ok?
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowTestConfirm(false)}
                className="rounded border border-n-200 bg-n-0 px-3 py-2 font-semibold text-[13px] text-n-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => testMut.mutate()}
                disabled={testMut.isPending}
                className="rounded bg-brand-500 px-3 py-2 font-semibold text-[13px] text-white hover:bg-brand-600 disabled:opacity-50"
              >
                {testMut.isPending ? 'Disparando…' : 'Disparar teste'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface SubToolbarProps {
  nodeCount: number;
  lastEditLabel: string | null;
  saveState: SaveState;
  saveLabel: string;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

function CanvasSubToolbar({
  nodeCount,
  lastEditLabel,
  saveState,
  saveLabel,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
}: SubToolbarProps) {
  return (
    <div
      // ReactFlow respeita position absolute via className; alvo: topo do canvas
      className="absolute top-2.5 left-2.5 right-2.5 z-[5] flex items-center gap-2 rounded-md border border-n-200 bg-n-0/90 px-3 py-2 shadow-sm backdrop-blur"
    >
      <div className="flex-1">
        <div className="font-semibold text-[12px] text-n-700">
          {nodeCount} nó{nodeCount === 1 ? '' : 's'}
        </div>
        <div className="mt-[1px] flex items-center gap-1.5 text-[11px] text-n-500">
          {lastEditLabel ? (
            <>
              <span>última edição {lastEditLabel}</span>
              <span>·</span>
            </>
          ) : null}
          <span
            className={
              saveState === 'error'
                ? 'text-danger-600'
                : saveState === 'dirty'
                  ? 'text-amber-600'
                  : saveState === 'saved'
                    ? 'text-success-700'
                    : 'text-n-500'
            }
          >
            {saveState === 'saving' && <Loader2 size={10} className="mr-1 inline animate-spin" />}
            {saveState === 'saved' && (
              <CheckCircle2 size={10} className="mr-1 inline text-success-500" />
            )}
            {saveLabel}
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={onUndo}
        disabled={!canUndo}
        className="flex h-8 items-center gap-1.5 rounded border border-n-200 bg-n-0 px-2.5 font-bold text-[11px] text-n-700 uppercase tracking-[.05em] hover:bg-n-50 disabled:cursor-not-allowed disabled:opacity-40"
        title="Desfazer (Ctrl+Z)"
      >
        <Undo2 size={13} strokeWidth={1.75} />
        Desfazer
      </button>
      <button
        type="button"
        onClick={onRedo}
        disabled={!canRedo}
        className="flex h-8 items-center gap-1.5 rounded border border-n-200 bg-n-0 px-2.5 font-bold text-[11px] text-n-700 uppercase tracking-[.05em] hover:bg-n-50 disabled:cursor-not-allowed disabled:opacity-40"
        title="Refazer (Ctrl+Shift+Z)"
      >
        <Redo2 size={13} strokeWidth={1.75} />
        Refazer
      </button>
    </div>
  );
}

function defaultConfigFor(kind: NodeKind): Record<string, unknown> {
  switch (kind) {
    case 'trigger':
      return { trigger_type: 'any_message' };
    case 'message':
      return { message_type: 'text', text: '' };
    case 'input':
      return {
        input_type: 'text',
        variable: '{{resposta}}',
        prompt: '',
        timeout_minutes: 5,
        on_timeout: 'end',
      };
    case 'condition':
      return { expression: '' };
    case 'condition_vars':
      return {
        source: 'message',
        variable: '',
        content: '{{message.body}}',
        branches: [{ comparator: 'eq', values: [] }],
      };
    case 'menu':
      return {
        prompt: '',
        options: [
          { op: 'equals', values: ['1'] },
          { op: 'equals', values: ['2'] },
        ],
        variable: '',
      };
    case 'retry':
      return { message: 'Não entendi. Responda com uma das opções.', max_attempts: 3 };
    case 'schedule_table':
      return { windows: [] };
    case 'transfer_human':
      return { tags: [] };
    case 'wait':
      return { mode: 'duration', seconds: 60 };
    case 'webhook':
      return { url: '', timeout_ms: 5000 };
    case 'end':
      return { close_ticket: false };
  }
}
